import Form from "common/Form/Form";
import Text from "common/Typography/Text";
import LoadingSpinnerOverlay from "common/Overlays/LoadingSpinnerOverlay";
import Input from "common/Inputs/Input";
import { SecondaryButton } from "features/configuration/common/styles";
import styled from "styled-components";
import QuickConnectForm from "features/configuration/accounts/CreateAccount/QuickConnectForm";
import SubmitButton from "features/onboarding/common/SubmitButton";
import useClientForm from "features/configuration/accounts/CreateAccount/useClientForm";
import { ClientFormProps } from "features/configuration/common/types";
import SelectSubExchange from "features/portfolio/CreateAccount/SelectSubExchange";
import ConnectExchangeGuide from "features/portfolio/CreateAccount/ConnectExchangeGuide";
import { DEMO_ACCOUNT_DEFAULT_INITIAL_FUNDS } from "@shared/features/portfolio/constants";
import { validateDemoAccountInitialFunds } from "@shared/features/configuration/utils";
import CustomInputNumber from "common/Inputs/CustomInputNumber";

const ClientForm = ({
  account,
  primaryExchange,
  onSubmitSuccess,
  closeDrawer,
  form,
  isOnboarding,
  getErrorNotification,
  isQuickConnect,
  getSuccessMessage,
}: ClientFormProps) => {
  const {
    isMobile,
    isEdit,
    showPassphrase,
    onFinish,
    whitelistIPs,
    quotaModal,
    mainButtonData,
    secondaryButtonData,
    isDemoWorkspace,
    validateApiInfo,
    isLoading,
    openConfirmModal,
    confirmModalContextHolder,
    accountSubExchange,
    isHyperLiquid,
  } = useClientForm({
    primaryExchange,
    account,
    onSubmitSuccess,
    closeDrawer,
    form,
    isOnboarding,
    getErrorNotification,
    getSuccessMessage,
  });

  const buttons = (
    <>
      <StyledSubmitButton
        onClick={form.submit}
        loading={mainButtonData.loading}
        disabled={mainButtonData.disabled}
        $isMobile={isMobile}
      >
        {mainButtonData.text}
      </StyledSubmitButton>
      {secondaryButtonData.text && (
        <SecondaryButton
          onClick={secondaryButtonData.onClick}
          style={{ marginTop: 16 }}
        >
          {secondaryButtonData.text}
        </SecondaryButton>
      )}
    </>
  );

  const manualConnectFormItems = (
    <>
      {isDemoWorkspace && !isEdit && (
        <Form.Item
          label="Initial Funds (USD)"
          name="demo_initial_funds"
          initialValue={DEMO_ACCOUNT_DEFAULT_INITIAL_FUNDS}
          rules={[
            () => ({
              validator(_, value) {
                const error = validateDemoAccountInitialFunds(value);
                if (error) return Promise.reject(error);
                else return Promise.resolve();
              },
            }),
          ]}
        >
          <CustomInputNumber />
        </Form.Item>
      )}
      {!isDemoWorkspace && (
        <>
          <Form.Item
            label={isHyperLiquid ? "Account Wallet Address" : "API Key"}
            name="api_key"
            rules={[
              () => ({
                validator() {
                  return validateApiInfo("api_key", "API key");
                },
              }),
            ]}
          >
            <Input placeholder={account ? account.api_key : ""} />
          </Form.Item>
          <Form.Item
            label={isHyperLiquid ? "API Private Key" : "API Secret"}
            name="api_secret"
            rules={[
              () => ({
                validator() {
                  return validateApiInfo("api_secret", "API secret");
                },
              }),
            ]}
          >
            <Input placeholder={account ? "*********" : ""} />
          </Form.Item>
          {showPassphrase && (
            <Form.Item
              label="API Passphrase"
              name="api_passphrase"
              rules={[
                () => ({
                  validator() {
                    return validateApiInfo("api_passphrase", "API passphrase");
                  },
                }),
              ]}
            >
              <Input placeholder={account ? "*********" : ""} />
            </Form.Item>
          )}
          {whitelistIPs}
        </>
      )}
      {buttons}
      {isMobile && !isOnboarding && (
        <ConnectExchangeGuide
          primaryExchange={primaryExchange}
          selectedSubExchange={accountSubExchange}
          isQuickConnect={isQuickConnect}
        />
      )}
    </>
  );

  const editAccountNameMessage = isEdit && !isDemoWorkspace && (
    <Text style={{ marginTop: 16 }}>
      To only update the account's name, please leave the API fields untouched.
    </Text>
  );

  return (
    <div>
      <LoadingSpinnerOverlay
        isLoading={isLoading}
        hideContentWhileLoading={true}
        wrap={true}
        spinnerMarginTop={100}
      >
        <Form
          form={form}
          name="clientForm"
          onFinish={onFinish}
          layout="vertical"
          requiredMark={false}
          style={{ width: "100%" }}
        >
          <SelectSubExchange
            primaryExchange={primaryExchange}
            account={account}
            isEdit={isEdit}
          />
          {(!isQuickConnect || isDemoWorkspace) && manualConnectFormItems}
        </Form>
        {editAccountNameMessage}
        {isQuickConnect && (
          <QuickConnectForm
            primaryExchange={primaryExchange}
            openConfirmModal={openConfirmModal}
          />
        )}
        {quotaModal}
        {confirmModalContextHolder}
      </LoadingSpinnerOverlay>
    </div>
  );
};

export default ClientForm;

const StyledSubmitButton = styled(SubmitButton)<{ $isMobile: boolean }>`
  margin: ${(p) => (p.$isMobile ? 0 : "12px 0 0 0")};
`;
